import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface PMShriConstants {
  totalSchools: number;
  totalKVs: number;
  totalPMSHRI: number;
  activeSchools: number;
  totalHeads: number;
  phases: (1 | 2 | 3 | 5 | 7)[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
}

const initialState: PMShriConstants = {
  totalSchools: 0, // Will be loaded from API
  totalKVs: 0, // Will be loaded from API
  totalPMSHRI: 0, // Will be loaded from API
  activeSchools: 0, // Will be calculated from totalSchools
  totalHeads: 6, // Fixed constant - always 6 heads
  phases: [1, 2, 3, 5, 7], // Fixed constant - PM SHRI phases
  isLoading: false,
  error: null,
  lastUpdated: null,
};

const pmShriConstantsSlice = createSlice({
  name: 'pmShriConstants',
  initialState,
  reducers: {
    setTotalSchools: (state, action: PayloadAction<number>) => {
      state.totalSchools = action.payload;
      state.activeSchools = Math.floor(action.payload * 0.94); // 94% active
      state.lastUpdated = Date.now();
    },
    setSchoolCounts: (state, action: PayloadAction<{ totalSchools?: number; totalKVs?: number; totalPMSHRI?: number }>) => {
      if (action.payload.totalSchools !== undefined) {
        state.totalSchools = action.payload.totalSchools;
        state.activeSchools = Math.floor(action.payload.totalSchools * 0.94);
      }
      if (action.payload.totalKVs !== undefined) {
        state.totalKVs = action.payload.totalKVs;
      }
      if (action.payload.totalPMSHRI !== undefined) {
        state.totalPMSHRI = action.payload.totalPMSHRI;
      }
      state.lastUpdated = Date.now();
    },
    setConstants: (state, action: PayloadAction<Partial<Omit<PMShriConstants, 'isLoading' | 'error' | 'lastUpdated'>>>) => {
      state.totalSchools = action.payload.totalSchools ?? state.totalSchools;
      state.totalKVs = action.payload.totalKVs ?? state.totalKVs;
      state.totalPMSHRI = action.payload.totalPMSHRI ?? state.totalPMSHRI;
      state.activeSchools = action.payload.activeSchools ?? state.activeSchools;
      state.totalHeads = action.payload.totalHeads ?? state.totalHeads;
      state.phases = action.payload.phases ?? state.phases;
      state.lastUpdated = Date.now();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { setTotalSchools, setSchoolCounts, setConstants, setLoading, setError } = pmShriConstantsSlice.actions;

// Selectors
export const selectTotalSchools = (state: any) => state.pmShriConstants.totalSchools;
export const selectTotalKVs = (state: any) => state.pmShriConstants.totalKVs;
export const selectTotalPMSHRI = (state: any) => state.pmShriConstants.totalPMSHRI;
export const selectActiveSchools = (state: any) => state.pmShriConstants.activeSchools;
export const selectTotalHeads = (state: any) => state.pmShriConstants.totalHeads;
export const selectPhases = (state: any) => state.pmShriConstants.phases;
export const selectPMShriConstants = (state: any) => state.pmShriConstants;

export default pmShriConstantsSlice.reducer;
